import {
  courierTemplateDesignerUrl,
  useCourierTemplatePreview,
} from '@commenda/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Separator,
} from '@commenda/kakashi';

interface CourierTemplatePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  templateId: string;
  templateName?: string;
}

// Best-effort, in-app skeleton preview of a Courier template (subject + body blocks). The fully
// styled render only exists in Courier, hence the "Open in Courier" escape hatch.
export const CourierTemplatePreviewModal = ({
  isOpen,
  onClose,
  templateId,
  templateName,
}: CourierTemplatePreviewModalProps) => {
  const { data: previewData, isLoading } = useCourierTemplatePreview(
    templateId || undefined,
    isOpen
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{templateName ?? 'Template preview'}</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <span className="text-text-secondary txt-medium-regular p-4">
            Loading preview...
          </span>
        ) : previewData && previewData.bodyAvailable ? (
          <div className="flex flex-col gap-3">
            {previewData.subject && (
              <div className="flex flex-col gap-1 p-4 border-b">
                <span className="txt-extra-small-regular text-text-secondary">
                  Subject
                </span>
                <span className="txt-medium-medium">{previewData.subject}</span>
              </div>
            )}
            <div className="flex flex-col gap-3 p-4">
              {previewData.blocks.map((block) =>
                block.type === 'divider' ? (
                  <Separator key={block.id} />
                ) : block.type === 'action' ? (
                  <div
                    key={block.id}
                    className="self-start rounded-md bg-bg-brand px-4 py-2 text-white txt-medium-medium"
                  >
                    {block.text || 'Button'}
                  </div>
                ) : block.type === 'image' ? (
                  <div
                    key={block.id}
                    className="rounded-md border border-stroke-3 p-3 txt-medium-regular text-text-secondary"
                  >
                    Image
                  </div>
                ) : (
                  <p
                    key={block.id}
                    className="txt-medium-regular whitespace-pre-wrap leading-relaxed"
                  >
                    {block.text}
                  </p>
                )
              )}
            </div>
          </div>
        ) : (
          <span className="text-text-secondary txt-medium-regular p-4">
            No in-app preview for this template (it uses a custom layout). Open
            it in Courier to view the full design.
          </span>
        )}

        <DialogFooter>
          <Button
            variant="link"
            onClick={() =>
              window.open(
                courierTemplateDesignerUrl(templateId),
                '_blank',
                'noopener,noreferrer'
              )
            }
          >
            Open in Courier ↗
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
